import { Injectable } from '@angular/core';
import { CacheListener } from './@utils/cache-listener';
import { Vehicle } from 'interfaces/common/db/models/vehicle';
import type { ParsedVehiclesCSV } from 'interfaces/common/lib/modules/vehicles/parseVehiclesCSVFile';
import type { CreateManyVehiclesResult } from 'interfaces/common/lib/modules/vehicles/createManyVehicles';
import type { CreateManyVehiclesPayload } from 'interfaces/modules/vehicle/http/createManyVehicles.endpoint';
import type { RemoveManyVehiclesResult } from 'interfaces/modules/vehicle/http/removeMany.endpoint';
import { AuthService } from '@services';
import dexFreight from './microservices';
import { Subscription } from 'rxjs';

export interface VehicleType {
  ckey: string;
  description: string;
  numberAxes: string;
  numberTires: string;
  trailer: string;
}

/** Una entrada del catálogo `c-permission-type` del SAT (`PermSCT`). */
export interface PermissionType {
  key: string;
  description: string;
}

@Injectable({
  providedIn: 'root'
})
export class VehiclesService {

  vehicles: CacheListener<Vehicle[]> = new CacheListener("vehicles", [], this.syncVehicles.bind(this));
  vehicleTypes: CacheListener<VehicleType[]> = new CacheListener("vehicleTypes", [], this.syncVehicleTypes.bind(this));
  /**
   * El catálogo del permiso SICT. Vive aquí y no en la vista porque el permiso
   * pasó a ser un dato del vehículo, y porque un componente no habla con el
   * backend — es lo que el linter de componentes comprueba.
   */
  permissionTypes: CacheListener<PermissionType[]> = new CacheListener("permissionTypes", [], this.syncPermissionTypes.bind(this));
  listeners: Subscription[] = [];

  constructor(
    private auth: AuthService
  ) { }

  async all () {
    return await this.vehicles.get();
  }

  setupListeners() {
    this.listeners.push(
      //* When the token changes, sync the vehicles
      this.vehicles.syncWhenChanges(this.auth.token),
      this.vehicleTypes.syncWhenChanges(this.auth.token),
      this.permissionTypes.syncWhenChanges(this.auth.token)
    );
  }

  removeListeners() {
    this.listeners.forEach(listener => listener.unsubscribe());
    this.listeners = [];
  }

  async syncVehicles() {
    const token = await this.auth.token.get();
    if (!token) return [];

    const vehicles = await dexFreight.Vehicles.getAll(token);

    return vehicles.reverse();
  }

  async syncVehicleTypes() {
    const token = await this.auth.token.get();
    if (!token) return [];

    const vehicleTypes = await dexFreight.Vehicles.selectVehicle(token);

    return vehicleTypes;
  }

  async syncPermissionTypes() {
    const token = await this.auth.token.get();
    if (!token) return [];

    return await dexFreight.Vehicles.getPermissionType(token) as PermissionType[];
  }

  async trailerTypes() {
    const token = await this.auth.token.get();
    if (!token) return [];

    return await dexFreight.Vehicles.getAllSubTipoRem(token);
  }

  async updateVehicle (vehicleId: string, vehicle: Partial<Vehicle>, { sync = true } = {}) {
    const token = await this.auth.token.get();
    if (!token) return;

    // La respuesta se devuelve: con una póliza de R.C. atada trae
    // `insuranceFromPolicy`, y el formulario lo explica si descartó lo tecleado.
    const result = await dexFreight.Vehicles.update(vehicleId, vehicle, token);
    if (sync) await this.vehicles.sync();

    return result;
  }

  async createVehicle (data: Omit<Vehicle, "_id">, { sync = true } = {}) {
    const token = await this.auth.token.get();
    if (!token) return;

    const vehicle = await dexFreight.Vehicles.create(data, token);
    if (sync) await this.vehicles.sync();

    return vehicle;
  }

  async removeVehicle (vehicleId: string, { sync = true } = {}) {
    const token = await this.auth.token.get();
    if (!token) return;

    await dexFreight.Vehicles.remove(vehicleId, token);
    if (sync) await this.vehicles.sync();
  }

  /**
   * Baja en bloque. El servidor da de baja lo que puede y devuelve, una por
   * una, las que no pudo y por qué: una unidad que falla no detiene a las demás.
   * Se sincroniza una vez al final, igual que el alta por lote.
   */
  async removeVehicles (vehicleIds: string[], { sync = true } = {}): Promise<RemoveManyVehiclesResult> {
    const token = await this.auth.token.get();
    if (!token) return { removed: [], failed: [] };

    const result = await dexFreight.Vehicles.removeMany(vehicleIds, token) as RemoveManyVehiclesResult;
    if (sync) await this.vehicles.sync();

    return result;
  }

  // ── Carga masiva por CSV ────────────────────────────────────────────────
  //
  // El parseo del CSV vive en el backend, como el resto del producto: el
  // cliente lee el archivo como texto y lo manda. Así hay un solo tokenizador
  // de CSV en dexCarga y no entra una segunda librería en el navegador.

  /** Lee el archivo y devuelve sus filas mapeadas. No escribe nada. */
  async parseVehiclesCSV (content: string): Promise<ParsedVehiclesCSV> {
    const token = await this.auth.token.get();
    if (!token) return { headers: null, rows: [] };

    return await dexFreight.Vehicles.parseVehiclesCSV(content, token) as ParsedVehiclesCSV;
  }

  /**
   * Las entradas de `c-config-autotransport` (clave y descripción). El CSV trae
   * la clave, pero se guarda la descripción: es contra lo que el timbrado
   * re-resuelve el tipo por coincidencia exacta.
   *
   * Sale del mismo `CacheListener` que alimenta el `<dxc-select>` del alta
   * manual, y esa es la razón de que la revisión del CSV no consulte nada al
   * servidor: es una lista única y global, no una que dependa de otro campo.
   */
  async vehicleTypeEntries (): Promise<{ ckey: string; description: string }[]> {
    const types = await this.vehicleTypes.get();

    return (types ?? [])
      .map(type => ({
        ckey: String(type?.ckey ?? '').trim(),
        description: String(type?.description ?? '').trim(),
      }))
      .filter(type => !!type.description);
  }

  /**
   * Las entradas de `c-permission-type`, que es contra lo que se coteja el
   * permiso SICT del CSV.
   *
   * Devuelve la clave **y** la descripción, no una sola de las dos: se guarda la
   * clave —es la que viaja al PAC como `PermSCT`— pero un archivo puede traer
   * cualquiera de las dos, o el «CLAVE - Descripción» que el selector del alta
   * manual enseña en pantalla y que es lo que alguien copia de ahí.
   *
   * Sale del mismo `CacheListener` que alimenta ese selector, y esa es la razón
   * de que la revisión del CSV no consulte nada al servidor.
   */
  async permissionTypeEntries (): Promise<{ key: string; description: string }[]> {
    const types = await this.permissionTypes.get();

    return (types ?? [])
      .map(type => ({
        key: String(type?.key ?? '').trim(),
        description: String(type?.description ?? '').trim(),
      }))
      .filter(type => !!type.key);
  }

  /**
   * Alta por lote. **Nunca sobrescribe**: una placa que ya existe se aparta en
   * el servidor, y el llamador ya excluye esas filas antes de mandarlas.
   *
   * No sincroniza la caché aquí: un archivo de doscientas unidades va en ocho
   * tandas, y volver a pedir la flota entera después de cada una haría ocho
   * consultas para enseñar el mismo listado. Sincroniza el llamador, una vez,
   * al terminar.
   */
  async createManyVehicles (vehicles: CreateManyVehiclesPayload[]): Promise<CreateManyVehiclesResult> {
    const token = await this.auth.token.get();

    return await dexFreight.Vehicles.createManyVehicles(vehicles, token) as CreateManyVehiclesResult;
  }
}
